import { translationKey } from '../i18n';
import { localize } from '../i18n';
import { SKIP_REASON_OPTIONS } from '../utils/skipReasonCopy';
import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Box, TextField } from '@mui/material';
import BottomSheet from './BottomSheet';
import LoadingLabel from './LoadingLabel';
import Button from './ui/FitButton';
import SingleLineText from './ui/SingleLineText';
import { colors, sheetSpacing, FONT_FAMILY } from '../design/tokens';

const REASONS = SKIP_REASON_OPTIONS;

export default function SkipSheet({ open, logs, bulk = false, reason, onReasonChange, processing, onClose, onSubmit }) {
    const titleId = useId();
    const reasonLabelId = useId();
    const customInputId = useId();
    const [custom, setCustom] = useState(false);
    const [customDraft, setCustomDraft] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const customInputRef = useRef(null);
    const busy = processing || submitting;

    useLayoutEffect(() => {
        if (!open) return;
        setCustom(false);
        setCustomDraft('');
        setSubmitting(false);
        submittingRef.current = false;
    }, [open]);

    const submit = async () => {
        if (busy || submittingRef.current || !logs.length) return;
        submittingRef.current = true;
        setSubmitting(true);
        try {
            await onSubmit();
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    return (
        <BottomSheet open={open} onClose={onClose} dismissDisabled={busy} ariaLabelledBy={titleId}
            footer={
                <Button fullWidth variant="contained" disableElevation onMouseDown={e => e.preventDefault()} onClick={submit}
                    disabled={busy || !logs.length} sx={{
                        bgcolor: colors.brand, color: colors.brandText, borderRadius: 3,
                        py: 1.5, fontSize: '1.1rem', fontWeight: 700,
                        '&:hover': { bgcolor: colors.brandHover },
                        '&:disabled': { bgcolor: colors.disabled, color: colors.textMuted },
                    }}>
                    {busy ? <LoadingLabel /> : bulk ? localize("{v0}개 함께 건너뛰기", { v0: logs.length }) : localize('이번 회차 건너뛰기')}
                </Button>
            }>
            <SingleLineText id={titleId} component="h2" sx={{
                fontSize: '1.2rem', fontWeight: 800, letterSpacing: '-0.01em',
                color: '#1F1D1B', fontFamily: FONT_FAMILY, mb: '4px',
            }}>
                {bulk ? localize("{v0}개 모두 건너뛰기", { v0: logs.length }) : localize('건너뛰기')}
            </SingleLineText>
            <SingleLineText id={reasonLabelId} sx={{
                fontSize: '0.95rem', color: '#7A766F', fontFamily: FONT_FAMILY,
                lineHeight: 1.45, mb: sheetSpacing.section,
            }}>{localize("사유 (선택)")}</SingleLineText>
            <Box role="group" aria-labelledby={reasonLabelId} sx={{
                display: 'flex', flexWrap: 'wrap', gap: '6px',
            }}>
                {REASONS.map(label => {
                    const isCustom = label === translationKey('직접 입력');
                    const selected = isCustom ? custom : !custom && reason === label;
                    return <Button key={label} aria-pressed={selected} disabled={busy}
                        aria-expanded={isCustom ? custom : undefined}
                        aria-controls={isCustom && custom ? customInputId : undefined}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                            if (isCustom) {
                                // Mount and focus within the tap so mobile keyboards keep the user gesture.
                                flushSync(() => {
                                    setCustom(true);
                                    onReasonChange(customDraft);
                                });
                                customInputRef.current?.focus();
                                return;
                            }
                            customInputRef.current?.blur();
                            setCustom(false);
                            onReasonChange(selected ? '' : label);
                        }} sx={{
                            minWidth: 0, maxWidth: '100%', minHeight: 48,
                            px: 1.25, py: 1, borderRadius: '999px',
                            border: `1px solid ${selected ? 'transparent' : '#EDE8DD'}`,
                            fontSize: '1rem', fontWeight: 600, letterSpacing: 'normal',
                            bgcolor: selected ? colors.brand : colors.surface,
                            color: selected ? colors.brandText : '#1F1D1B',
                            '&:hover': { bgcolor: selected ? colors.brand : '#F8F5EE' },
                            '&:active': { bgcolor: selected ? colors.brandHover : '#F8F5EE' },
                            '&.Mui-focusVisible': { outline: `2px solid ${colors.brandDark}`, outlineOffset: 2 },
                            '&.Mui-disabled': { opacity: 0.6 },
                        }}>
                        <SingleLineText component="span" sx={{ font: 'inherit' }}>{localize(label)}</SingleLineText>
                    </Button>;
                })}
            </Box>
            {custom && <TextField id={customInputId} fullWidth multiline minRows={3} maxRows={5}
                placeholder={localize("어떤 이유인지 편하게 적어주세요")}
                inputRef={customInputRef} value={customDraft} disabled={busy}
                inputProps={{ 'aria-label': localize('건너뛰는 이유'), 'aria-describedby': reasonLabelId, 'data-keyboard-lift-action': 'true' }}
                onChange={e => { setCustomDraft(e.target.value); onReasonChange(e.target.value); }}
                sx={{ mt: sheetSpacing.section,
                    '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#f9fafb', minHeight: 52, p: 1.75,
                        '& fieldset': { borderColor: '#e9ebef' },
                        '&:hover fieldset': { borderColor: '#d6d8dd' },
                        '&.Mui-focused fieldset': { borderColor: colors.brandDark, borderWidth: 2 },
                    },
                    '& textarea': { fontSize: '1rem', lineHeight: 1.6,
                        '&::placeholder': { color: '#7a8089', opacity: 1 },
                    },
                }} />}
            <SingleLineText sx={{ mt: 1.5, fontSize: '0.875rem', color: colors.textSecondary }}>{localize("사유 없이도 건너뛸 수 있어요.")}</SingleLineText>
        </BottomSheet>
    );
}
